import { DatePipe, registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import { CSP_NONCE, LOCALE_ID, NgModule, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MedpaceDeleteCaregiverModalComponent } from '@app/admin/manage/modals/delete-caregiver-modal/delete-caregiver-modal.component';
import { MedpaceDeleteInfoCaregiverModalComponent } from '@app/admin/manage/modals/delete-info-caregiver-modal/delete-info-caregiver-modal.component';
import { MedpaceDraggableListWithCheckboxesComponent } from '@app/components/atoms/medpace-draggable-list-with-checkboxes/medpace-draggable-list-with-checkboxes.component';
import { MedpaceDraggableListComponent } from '@app/components/atoms/medpace-draggable-list/medpace-draggable-list.component';
import { AsyncActionButtonDirective } from '@app/directives/async-action-button/async-action-button.directive';
import { DecimalNumberDirective } from '@app/directives/decimal-number/decimal-number.directive';
import { MarkAsteriskDirective } from '@app/directives/mark-asterisk/mark-asterisk.directive';
import { HistoryTimelineComponent } from '@components/atoms/history-timeline/history-timeline.component';
import { MedpaceUploadComponent } from '@components/atoms/medpace-upload/medpace-upload.component';
import { SelectedCustomDatePickerComponent } from '@components/atoms/selected/selected-custom-datepicker.component';
import { SelectedCustomDropDownComponent } from '@components/atoms/selected/selected-custom-dropdown.component';
import { MedpaceTextareaAutocompleteComponent } from '@components/atoms/textarea-autocomplete/medpace-textarea-autocomplete.component';
import { CaregiverInfoElementComponent } from '@components/molecules/forms/caregiver-info-card/caregiver-info-element/caregiver-info-element.component';
import { InformationAccuracyModalComponent } from '@components/molecules/forms/patient-payment-card/bank-details/information-accuracy-modal/information-accuracy-modal.component';
import { MedpacePatientTravelPreferencesCardComponent } from '@components/molecules/forms/patient-travel-card/travel-card.component';
import { MedpaceTableComponent } from '@components/molecules/table/table.component';
import { PaymentRequestCardComponent } from '@components/request/payment-request-card/payment-request-card.component';
import { TravelCardComponent } from '@components/request/travel-request-card/travel-request-card.component';
import { StudyExpenseTrackerComponent } from '@components/study-expense-tracker/study-expense-tracker.component';
import { environment } from '@environments/environment';
import { AdminUserRepository } from '@repositories/admin/admin-user.repository';
import { UserClient } from '@repositories/admin/user-client.repository';

import { CaregiverRepository } from '@repositories/caregiver/caregiver.repository';
import { GetThereRepository } from '@repositories/getthere/getthere.repository';
import { AccessGroupClient } from '@repositories/group/access-group-client.repository';

import { StudyPaymentApprovalThresholdCardComponent } from '@app/v2/components/study-payment-approval-threshold-card/study-payment-approval-threshold-card.component';
import { MedpaceCaregiverInfoDialogComponent } from '@components/molecules/forms/caregiver-info-card/caregiver-info-dialog/caregiver-info-dialog.component';
import { NoteStore } from '@components/molecules/forms/note-card/note.store';
import { MedpaceSiteAddressComponent } from '@components/molecules/forms/site-address/site-address.component';
import { MedpaceSiteShippingAddressComponent } from '@components/molecules/forms/site-shipping-address/site-shipping-address.component';
import { VerticalStepperLinkComponent } from '@components/molecules/forms/vertical-stepper-link/vertical-stepper-link.component';
import { VerticalStepperComponent } from '@components/organisms/stepper/vertical-stepper.component';
import { AllEnterpriseModule, ModuleRegistry } from '@medpacesoftwaredevelopment/designsystem/lib/components/ag-grid';
import { WaResizeObserver } from '@ng-web-apis/resize-observer';
import { CaregiverService } from '@services/caregiver/caregiver.service';
import { EnvConfigurationService } from '@services/env/env-configuration.service';
import { PdfGenerationService } from '@services/pdf-generation/pdf-generation.service';
import { ReimbursementRequestStateService } from '@services/state-management/reimbursement-request-state.service';
import { StipendRequestStateService } from '@services/state-management/stipend-request-state.service';
import { TimeOptionsStateService } from '@services/state-management/time-options-state.service';
import { TravelRequestStateService } from '@services/state-management/travel-request-state.service';
import { OAuthModule } from 'angular-oauth2-oidc';
import { DisableMouseScrollBehaviorDirective } from 'src/app/directives/disable-mouse-scroll-behavior/disable-mouse-scroll-behavior.directive';
import { ScrollToInvalidControlOnSubmitDirective } from 'src/app/directives/scroll-to-invalid-control-on-submit/scroll-to-invalid-control-on-submit.directive';
import { SiteService } from 'src/app/services/site/site.service';
import { MaterialModule } from '../app/shared/material.module';
import { SuperAdminDirective } from '././directives/super-admin/super-admin.directive';
import { DownloadAttachmentModalComponent } from './admin/manage/modals/download-attachment-modal/download-attachment-modal.component';
import { GenerateStipendRequestModalComponent } from './admin/manage/modals/generate-stipend-request-modal/generate-stipend-request-modal.component';
import { LoadingComponent } from './admin/manage/modals/loading-component/loading.component';
import { MedpacePatientChangeStatusModalComponent } from './admin/manage/modals/patient-change-status-modal.component';
import { SendReminderModalComponent } from './admin/manage/modals/send-reminder-modal/send-reminder-modal.component';
import { MedpaceSiteChangeStatusModalComponent } from './admin/manage/modals/site-change-status-modal/site-change-status-modal.component';
import { CreateUserManagementModalComponent } from './admin/manage/modals/user-management/create-user-management-modal/create-user-management-modal.component';
import { UpdateUserManagementModalComponent } from './admin/manage/modals/user-management/update-user-management-modal/update-user-management-modal.component';
import { MedpaceUserOperationAlertModalComponent } from './admin/manage/modals/user-operation-alert-modal/user-operation-alert-modal.component';
import { MedpaceViewHistoryModalComponent } from './admin/manage/modals/view-history-modal.component';
import { API_BASE_URL } from './API_BASE_URL';
import { AppRoutingModule, registeredComponents } from './app-routing.module';
import { AppComponent } from './app.component';
import { AlertComponent } from './components/atoms/alert/alert.component';
import {
    MedpaceCardComponent,
    MedpaceCardContentDirective,
    MedpaceCardHeaderDirective,
} from './components/atoms/medpace-card/medpace-card.component';
import { MedpaceChipListComponent } from './components/atoms/medpace-chip-list/medpace-chip-list.component';
import { MedpaceDatepickerComponent } from './components/atoms/medpace-datepicker/medpace-datepicker.component';
import {
    MedpaceEditableCardComponent,
    MedpaceEditableCardEditModeContentDirective,
    MedpaceEditableCardHeaderDirective,
    MedpaceEditableCardViewModeContentDirective,
} from './components/atoms/medpace-editable-card/medpace-editable-card.component';
import { MedpaceMatChipListComponent } from './components/atoms/medpace-mat-chip-list/medpace-mat-chip-list.component';
import { RequestHistoryPostComponent } from './components/atoms/request-history/request-history-post/request-history-post.component';
import { RequestHistoryComponent } from './components/atoms/request-history/request-history.component';
import { CRCCard2Component } from './components/molecules/forms/crc-card2/crc-card2.component';
import { MedpaceCreateRequestVisitDetailsComponent } from './components/molecules/forms/medpace-create-request-visit-details/medpace-create-request-visit-details.component';
import { MedpaceEditRequestVisitDetailsComponent } from './components/molecules/forms/medpace-edit-request-visit-details/medpace-edit-request-visit-details.component';
import { NoteCardComponent } from './components/molecules/forms/note-card/note-card.component';
import { MedpaceVisitMappingModalComponent } from './components/molecules/modals/medpace-visit-mapping-modal/medpace-visit-mapping-modal.component';
import { SiteRegisterModalComponent } from './components/molecules/modals/site-register-modal/site-register-modal.component';
import { GlobalContactNumbersComponent } from './components/organisms/global-contact-numbers/global-contact-numbers.component';
import { SimpleCardComponent } from './components/organisms/simple-card/simple-card.component';
import { ErrorSnackbarComponent } from './components/organisms/snackBar/error-snackbar/error-snackbar.component';
import { InfoSnackbarComponent } from './components/organisms/snackBar/info-snackbar/info-snackbar.component';
import { AdminDirective } from './directives/admin/admin.directive';
import { ExpansionFormErrorHighlighterDirective } from './directives/expansion-form-error-highlighter.directive';
import { FormErrorHighlighterDirective } from './directives/form-error-highlighter.directive';
import { GoogleMapsAutocompleteDirective } from './directives/google-maps-autocomplete/google-maps-autocomplete.directive';
import { ApiModule } from './generated-api/api.module';
import { httpInterceptorProviders } from './http-interceptors/index';
import { dateFormatPipe } from './pipes/datepipe';
import { AccessRequestRepository } from './repositories/accessRequest/access-request.repository';
import { AdminPatientRepository } from './repositories/admin/admin-patient.repository';
import { AdminRequestRepository } from './repositories/admin/admin-request.repository';
import { AdminSiteRepository } from './repositories/admin/admin-site.repository';
import { AdminStudyRepository } from './repositories/admin/admin-study.repository';
import { ExpenseRepository } from './repositories/admin/expense.repository';
import { CRCSiteRepository } from './repositories/crc/crc-sites.repository';
import { CRCStudyRepository } from './repositories/crc/crc-studies.repository';
import { FormDataRepository } from './repositories/form-data.repository';
import { GroupRepository } from './repositories/group/group.repository';
import { PcsConfigurationRepository } from './repositories/pcsConfiguration/pcs-configuration.repository';
import { ReportRepository } from './repositories/reports/reports.repository';
import { AccessRequestService } from './services/access-request/access-request.service';
import { AdminPatientServices } from './services/admin/admin-patient.service';
import { AdminRequestServices } from './services/admin/admin-request.service';
import { AdminSiteServices } from './services/admin/admin-site.service';
import { AdminStudyServices } from './services/admin/admin-study.sevice';
import { AttachmentSizeService } from './services/attachment-size/attachment-size.service';
import { AuthService } from './services/auth/auth.service';
import { CRCSiteServices } from './services/crc/crc-site.service';
import { CRCStudyServices } from './services/crc/crc-study.service';
import { ErrorHandlingModule } from './services/error-handling/error-handling.module';
import { FormDataService } from './services/form-data/form-data.service';
import { GetThereService } from './services/getthere/getthere.service';
import { GroupService } from './services/group/group.service';
import { ForcedLoginAuthGuard } from './services/guards/auth.guard';
import { PatientStatusService } from './services/patient-status/patient-status.service';
import { ReportServices } from './services/reports/reports.service';
import { PatientStateService } from './services/state-management/patient-state.service';
import { RequestStateService } from './services/state-management/request-state.service';
import { SiteStateService } from './services/state-management/site-state.service';
import { StudyStateService } from './services/state-management/study-state.service';
import { UserService } from './services/user/user.service';
import { XLSXService } from './services/xlsx/xlsx.service';
import { MdsModule } from './shared/mds.module';
import { DisplayErrorDialogComponent } from './v2/components/dialogs/display-error-dialog/display-error-dialog.component';
import { MessageDialogComponent } from './v2/components/dialogs/message-dialog/message-dialog.component';
import { FooterComponent } from './v2/components/footer/footer.component';
import { LoadingCardComponent } from './v2/components/loading-card/loading-card.component';
import { RequestPersonalInfoCardComponent } from './v2/components/request-personal-info-card/request-personal-info-card.component';
import { RequestSideNotesItemComponent } from './v2/components/request-side-notes/request-side-notes-item/request-side-notes-item.component';
import { RequestSideNotesComponent } from './v2/components/request-side-notes/request-side-notes.component';
import { SiteServicesComponent } from './v2/components/site-services/site-services.component';
import { AsyncActionV2ButtonDirective } from './v2/directives/async-action-button.directive';
import { MedpaceLogoutComponent } from './v2/pages/logout/logout.component';
registerLocaleData(localeEn, 'en-US');

ModuleRegistry.registerModules([AllEnterpriseModule]);

@NgModule({
    declarations: [
        AppComponent,
        registeredComponents,
        MedpaceVisitMappingModalComponent,
        DecimalNumberDirective,
        MarkAsteriskDirective,
        MedpaceDeleteCaregiverModalComponent,
        MedpaceSiteChangeStatusModalComponent,
        ErrorSnackbarComponent,
        InfoSnackbarComponent,
        SuperAdminDirective,
        AdminDirective,
        AsyncActionButtonDirective,
        MedpaceCardComponent,
        MedpaceCardHeaderDirective,
        MedpaceCardContentDirective,
        MedpaceEditableCardComponent,
        MedpaceUserOperationAlertModalComponent,
        MedpaceEditableCardEditModeContentDirective,
        MedpaceEditableCardViewModeContentDirective,
        MedpaceEditableCardHeaderDirective,
        MedpaceCreateRequestVisitDetailsComponent,
        MedpaceEditRequestVisitDetailsComponent,
        MedpacePatientChangeStatusModalComponent,
        DownloadAttachmentModalComponent,
        MedpaceChipListComponent,
        GlobalContactNumbersComponent,
        UpdateUserManagementModalComponent,
        CreateUserManagementModalComponent,
        CRCCard2Component,
        SimpleCardComponent,
        ExpansionFormErrorHighlighterDirective,
        MedpaceDeleteInfoCaregiverModalComponent,
        MedpaceDraggableListComponent,
        MedpaceDraggableListWithCheckboxesComponent,
        InformationAccuracyModalComponent,
        RequestHistoryComponent,
        RequestHistoryPostComponent,
        HistoryTimelineComponent,
        SendReminderModalComponent,
        NoteCardComponent,
        MedpaceMatChipListComponent,
    ],
    imports: [
        MedpaceViewHistoryModalComponent,
        VerticalStepperComponent,
        VerticalStepperLinkComponent,
        ScrollToInvalidControlOnSubmitDirective,
        MedpaceCaregiverInfoDialogComponent,
        MedpaceSiteAddressComponent,
        MedpaceSiteShippingAddressComponent,
        StudyExpenseTrackerComponent,
        ApiModule,
        BrowserModule,
        AppRoutingModule,
        HttpClientModule,
        FormsModule,
        ReactiveFormsModule,
        OAuthModule.forRoot({
            resourceServer: {
                allowedUrls: [`${environment.baseUrl}/api`],
                sendAccessToken: true,
            },
        }),
        BrowserAnimationsModule,
        MaterialModule,
        MdsModule,
        MatExpansionModule,
        MatMenuModule,
        MatButtonModule,
        MatIconModule,
        MatDividerModule,
        MatToolbarModule,
        ErrorHandlingModule,
        GoogleMapsModule,
        MatListModule,
        AlertComponent,
        PaymentRequestCardComponent,
        SiteServicesComponent,
        MedpaceUploadComponent,
        MedpaceDatepickerComponent,
        DisableMouseScrollBehaviorDirective,
        dateFormatPipe,
        TravelCardComponent,
        MedpacePatientTravelPreferencesCardComponent,
        FormErrorHighlighterDirective,
        LoadingCardComponent,
        MedpaceTextareaAutocompleteComponent,
        CaregiverInfoElementComponent,
        GenerateStipendRequestModalComponent,
        MedpaceTableComponent,
        SelectedCustomDropDownComponent,
        SelectedCustomDatePickerComponent,
        LoadingComponent,
        AsyncActionV2ButtonDirective,
        DisplayErrorDialogComponent,
        MessageDialogComponent,
        FooterComponent,
        MedpaceLogoutComponent,
        GoogleMapsAutocompleteDirective,
        RequestPersonalInfoCardComponent,
        WaResizeObserver,
        StudyPaymentApprovalThresholdCardComponent,
        SiteRegisterModalComponent,
        RequestSideNotesComponent,
        RequestSideNotesItemComponent,
    ],
    providers: [
        AuthService,
        ForcedLoginAuthGuard,
        GroupService,
        GroupRepository,
        AdminSiteRepository,
        AdminSiteServices,
        AdminStudyRepository,
        AdminStudyServices,
        AdminPatientRepository,
        AdminPatientServices,
        AdminUserRepository,
        FormDataRepository,
        FormDataService,
        AdminRequestRepository,
        AdminRequestServices,
        AccessRequestService,
        AccessRequestRepository,
        PcsConfigurationRepository,
        CRCStudyRepository,
        CRCStudyServices,
        CRCSiteRepository,
        CRCSiteServices,
        ReportServices,
        ReportRepository,
        ExpenseRepository,
        SiteService,
        httpInterceptorProviders,
        PatientStatusService,
        AttachmentSizeService,
        RequestStateService,
        StudyStateService,
        SiteStateService,
        PatientStateService,
        UserService,
        DatePipe,
        XLSXService,
        UserClient,
        AccessGroupClient,
        dateFormatPipe,
        { provide: API_BASE_URL, useValue: environment.baseUrl },
        GetThereService,
        GetThereRepository,
        { provide: LOCALE_ID, useValue: 'en-US' },
        TravelRequestStateService,
        StipendRequestStateService,
        ReimbursementRequestStateService,
        CaregiverRepository,
        CaregiverService,
        TimeOptionsStateService,
        PdfGenerationService,
        { provide: CSP_NONCE, useFactory: nonceFactory, deps: [EnvConfigurationService] },
        NoteStore,
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}

export function nonceFactory(): string {
    const env = inject(EnvConfigurationService);
    return env.configuration.nonce;
}
